import type { ModalProps } from '@heroui/react'
import { Button, cn, Modal, ModalBody, ModalContent, ModalHeader, Tooltip } from '@heroui/react'
import type { ModalAction, ModalData } from '@qse/constants'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import type { Variants } from 'framer-motion'
import { Maximize2, Minimize2, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import ModelControl from './'
import { ModalFullScreenContext } from './modal-fullscreen-context'

/**
 * Render the system modal container with dynamic content and header/actions.
 * This component orchestrates all modal variants from `ModelControl` and
 * exposes a single place to configure base modal behavior and transitions.
 */
function ModalComponent(): JSX.Element {
  const {
    toggleModel,
    toggle,
    modelActive,
    setModelActive,
    modalHeader,
    modalActions,
    modelContent,
    width,
    data,
    reset,
    scrollBehavior,
  } = useActiveModelStore()
  const config = data as ModalData

  const isFilePreview = modelActive === ModelControlEnum.FILE_PREVIEW

  // Full screen is per-opening UI state, not store state: it resets when the
  // modal closes or a different modal takes over, and survives nothing else.
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    if (!toggleModel) setIsFullScreen(false)
  }, [toggleModel])

  useEffect(() => {
    setIsFullScreen(false)
  }, [modelActive])

  const showExpandButton = !!(config?.modalHeader?.showExpandButton ?? modalHeader.showExpandButton)

  // Short, focused modals should have center-aligned titles
  const centerAlignedModals = [
    ModelControlEnum.CONFIRMATION,
    ModelControlEnum.UPDATE_PASSWORD,
    ModelControlEnum.TWO_FA_SETUP,
    ModelControlEnum.TWO_FA_VERIFY,
    ModelControlEnum.TWO_FA_DISABLE,
    ModelControlEnum.ACCESS_DENIED,
    ModelControlEnum.NAVIGATION_WARNING,
    ModelControlEnum.QUOTA_EXCEEDED,
    ModelControlEnum.TRANSFER_ADMIN,
    ModelControlEnum.DELETE_USER_CONFIRMATION,
    ModelControlEnum.DELETE_TENANT_CONFIRMATION,
  ]

  const shouldCenterAlign = modelActive && centerAlignedModals.includes(modelActive)

  const MODAL_MOTION_VARIANTS: Variants = {
    enter: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    exit: {
      y: isFilePreview ? 0 : -20,
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: 'easeIn',
      },
    },
  }

  const scrollBehaviorValue: ModalProps['scrollBehavior'] =
    (scrollBehavior as ModalProps['scrollBehavior']) ?? 'outside'

  const handleClose = async () => {
    // Check if there's a beforeClose validation
    if (config?.onBeforeClose) {
      try {
        const canClose = await config.onBeforeClose()

        if (!canClose) {
          return // Prevent closing if validation fails
        }
      } catch (error) {
        console.error('Error in onBeforeClose validation:', error)

        return // Prevent closing on error
      }
    }

    // Proceed with normal closing
    toggle()
  }

  const handleOpenChange = async (isOpen: boolean) => {
    // If modal is being closed
    if (!isOpen) {
      // Check if there's a beforeClose validation
      if (config?.onBeforeClose) {
        try {
          const canClose = await config.onBeforeClose()

          if (!canClose) {
            return // Prevent closing if validation fails
          }
        } catch (error) {
          console.error('Error in onBeforeClose validation:', error)

          return // Prevent closing on error
        }
      }

      // Proceed with normal closing
      toggle()
    }
  }

  return (
    <Modal
      hideCloseButton
      backdrop="blur"
      isDismissable={!config?.onBeforeClose}
      isOpen={toggleModel}
      placement="center"
      // HeroUI's size="full" supplies the edge-to-edge sizing; scrolling moves
      // inside the body so the header (and its collapse control) stays put.
      scrollBehavior={isFullScreen ? 'inside' : scrollBehaviorValue}
      size={isFullScreen ? 'full' : undefined}
      classNames={{
        base: isFullScreen
          ? 'overflow-hidden'
          : cn(isFilePreview ? 'max-w-full sm:m-0' : 'max-w-5xl overflow-hidden', width),
        header: isFullScreen ? 'rounded-none' : 'rounded-t-2xl',
        body: isFilePreview ? 'p-0' : isFullScreen ? 'rounded-none' : 'rounded-b-2xl',
      }}
      motionProps={{
        variants: MODAL_MOTION_VARIANTS as any,
      }}
      onOpenChange={handleOpenChange}
    >
      <ModalContent>
        {!isFilePreview && config?.modalHeader !== null && (
          <ModalHeader
            className={`flex bg-gray-50 dark:bg-[#323232] border-b border-gray-200 dark:border-gray-700 items-center ${shouldCenterAlign ? 'justify-center' : 'justify-between'} gap-1 ${modalHeader.className || ''}`}
          >
            <div
              className={`flex flex-col gap-1 ${shouldCenterAlign ? 'items-center text-center' : ''}`}
            >
              {config?.modalHeader?.titleTooltip || modalHeader.titleTooltip ? (
                <Tooltip
                  content={config?.modalHeader?.titleTooltip || modalHeader.titleTooltip}
                  delay={500}
                >
                  <h2 className="text-2xl font-medium text-gray-900 dark:text-white cursor-hover">
                    {config?.modalHeader?.title || modalHeader.title}
                  </h2>
                </Tooltip>
              ) : (
                <h2 className="text-2xl font-medium text-gray-900 dark:text-white break-words max-w-[calc(100%-48px)] lg:max-w-full">
                  {config?.modalHeader?.title || modalHeader.title}
                </h2>
              )}
              {(config?.modalHeader?.subtitle || modalHeader.subtitle) && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {config?.modalHeader?.subtitle || modalHeader.subtitle}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {showExpandButton && (
                <Button
                  isIconOnly
                  aria-label={isFullScreen ? 'Exit full screen' : 'Expand to full screen'}
                  className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                  radius="full"
                  size="sm"
                  onPress={() => setIsFullScreen(prev => !prev)}
                >
                  {isFullScreen ? (
                    <Minimize2 className="w-[18px] h-[18px] text-gray-600 dark:text-white" />
                  ) : (
                    <Maximize2 className="w-[18px] h-[18px] text-gray-600 dark:text-white" />
                  )}
                </Button>
              )}

              {(config?.modalHeader?.showCloseButton ?? modalHeader.showCloseButton) && (
                <Button
                  isIconOnly
                  aria-label="Close"
                  className={`bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 ${shouldCenterAlign ? 'absolute right-4' : ''}`}
                  radius="full"
                  size="sm"
                  onPress={handleClose}
                >
                  <X className="w-[20px] h-[20px] text-gray-600 dark:text-white" />
                </Button>
              )}
            </div>
          </ModalHeader>
        )}

        <ModalFullScreenContext.Provider value={isFullScreen}>
          <ModalBody className="bg-white dark:bg-[#282828]">
            {/* Show existing model components if modelActive is set */}
            {modelActive ? (
              <ModelControl
                control={modelActive}
                data={data}
                reset={reset}
                setModelActive={setModelActive}
                setToggleModel={toggle}
                toggleModel={toggleModel}
                onClose={handleClose}
              />
            ) : (
              /* Show dynamic content if modelContent is set */
              modelContent && (
                <div className={isFilePreview ? 'py-0' : 'py-4'}>
                  <div dangerouslySetInnerHTML={{ __html: modelContent }} />
                </div>
              )
            )}

            {/* Dynamic Actions - only show if no modelActive (existing components handle their own actions) */}
            {!modelActive && modalActions.length > 0 && (
              <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                {modalActions.map((action: ModalAction, index: number) => (
                  <Button
                    key={index}
                    color={action.color || 'primary'}
                    isDisabled={action.disabled}
                    isLoading={action.isLoading}
                    size={action.size || 'md'}
                    variant={action.variant || 'solid'}
                    onPress={action.onPress}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </ModalBody>
        </ModalFullScreenContext.Provider>
      </ModalContent>
    </Modal>
  )
}

export default ModalComponent
